import { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, Menu, MenuItem, Select, Tab, Tabs, TextField } from "@mui/material";
import { FaArchive, FaEllipsisV, FaSave, FaTrash } from "react-icons/fa";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { FaPencil } from "react-icons/fa6";
import { queryClient } from "queryClient";
import { Task, TaskStatus } from "interfaces/entities/task.model";
import { toNativeDate } from "helpers/dateHelper";
import tasksService from "services/admin/tasksService";
import { TaskMainInfo } from "./TaskMainInfo";
import { useTranslation } from "react-i18next";
import { useGetMembers } from "hooks/useGetMembers";

const getStatusColor = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.TODO:
      return 'info'
    case TaskStatus.IN_PROGRESS:
      return 'warning'
    case TaskStatus.DONE:
      return 'success'
    default:
      return 'primary'
  }
}

interface TaskFormProps {
  task: any | null
  onSave: (taskInput: { isNew: boolean, task: Task | null }) => void
  pipelines: any[]
  defaultPipelineID?: number
}

export const TaskForm = ({ task, onSave, pipelines, defaultPipelineID }: TaskFormProps) => {
  const taskID = task?.id
  const { t } = useTranslation()
  const methods = useForm({
    defaultValues: {
      title: task?.title || '',
      description: task?.description || '',
      dueDate: task?.dueDate ? toNativeDate(task.dueDate) : toNativeDate(new Date()),
      assigneeID: task?.assigneeID,
      status: task?.status || TaskStatus.TODO,
      leadID: task?.leadID,
      pipelineID: task?.pipelineID ?? defaultPipelineID,
    },
    mode: "onChange",
  })

  const [moreOptionsAnchorEl, setMoreOptionsAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTab, setSelectedTab] = useState('main');


  // Dialogs
  const [isOpenDeleteTask, setIsOpenDeleteTask] = useState(false);
  const [isEditName, setIsEditName] = useState(false);


  // Dictionaries
  const { data: membersResponse } = useGetMembers(localStorage.getItem('orgID') || '')

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMoreOptionsAnchorEl(event.currentTarget);
  };


  const onSubmit = async (formData: any) => {
    try {
      if (task?.id) {
        const updatedTask = await tasksService.put(task.id, formData);

        queryClient.invalidateQueries({ queryKey: ['tasks'] })
        onSave({ isNew: false, task: updatedTask });
      } else {
        const newTask = await tasksService.post(formData);
        queryClient.invalidateQueries({ queryKey: ['tasks'] })

        onSave({ isNew: true, task: newTask });
      }
    } catch (error) {
      console.error(error);
      toast.error("Ошибка при обновлении сделки!");
    }
  }


  const onDeleteTask = async () => {
    console.log("taskID:", taskID)
    setIsOpenDeleteTask(false)

    try {
      await tasksService.delete(taskID);

      queryClient.invalidateQueries({ queryKey: ['tasks', taskID] })
      onSave({ isNew: false, task: null });
    } catch (error) {
      toast.error("Ошибка при удалении задачи!");
      console.error(error)
    }
  }

  const archiveOrRestoreTask = async () => {
    const newStatus = task?.status === TaskStatus.DONE ? TaskStatus.TODO : TaskStatus.DONE
    try {
      if (!taskID) {
        toast.error("Задача не найдена!");
        return;
      }

      if (!window.confirm(`Вы уверены, что хотите ${newStatus === TaskStatus.DONE ? 'завершить' : 'восстановить'} эту задачу?`)) {
        return;
      }

      await tasksService.patch(taskID, { status: newStatus } as any)
      queryClient.invalidateQueries({ queryKey: ['tasks', taskID] })
      onSave({ isNew: false, task: null });
      toast.success(`Задача успешно ${newStatus === TaskStatus.DONE ? 'завершена' : 'восстановлена'}!`);
    } catch (error) {
      toast.error(`Ошибка при ${newStatus === TaskStatus.DONE ? 'завершении' : 'восстановлении'} задачи!`);
      console.error(error)
    }
  }

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <section id="lead-detail">
            <div className="lead-header flex flex-space-between items-center">
              <div className="lead-title text-lg font-bold">
                {isEditName || !taskID ? (
                  <div>
                    <TextField
                      style={{ minWidth: '300px' }}
                      placeholder="Задача #"
                      size="small" {...methods.register('title')} fullWidth />
                  </div>

                ) : (
                  <span onClick={() => setIsEditName(true)} className="cursor-pointer">
                    {task?.title}
                    <IconButton onClick={() => setIsEditName(false)}>
                      <FaPencil size={12} />
                    </IconButton>
                  </span>
                )}
                <span className="text-sm text-gray-500 ml-2">#{task?.id}</span>
              </div>
              <div className="lead-actions flex gap-2">
                <Button size="small" variant="outlined" color="primary" type="submit">
                  <FaSave size={12} className="mr-2" />
                  Сохранить
                </Button>
                <Button size="small" variant="outlined" color="info" onClick={archiveOrRestoreTask}>
                  <FaArchive size={12} className="mr-2" />
                  {task?.status === TaskStatus.DONE ? 'Восстановить' : 'Завершить'}
                </Button>
                <Button size="small" variant="outlined" color="info" onClick={handleOpenMenu}>
                  <FaEllipsisV size={12} />
                </Button>
                <Menu
                  anchorEl={moreOptionsAnchorEl}
                  open={Boolean(moreOptionsAnchorEl)}
                  onClose={() => setMoreOptionsAnchorEl(null)}
                >
                  <MenuItem onClick={() => setIsOpenDeleteTask(true)}>
                    <div className="flex items-center justify-between w-100">
                      <div className="flex items-center gap-2">
                        <FaTrash size={12} />
                        Удалить задачу
                      </div>
                    </div>
                  </MenuItem>
                </Menu>
              </div>
            </div>
            <div className="lead-funnel mt-4 flex gap-2">
              <Controller control={methods.control} name="pipelineID" render={({ field }) => (
                <FormControl>
                  <InputLabel>Доска</InputLabel>
                  <Select style={{
                    width: 200,
                    maxWidth: 200,
                  }} label="Доска" size="small" {...field}>
                    {pipelines?.map((pipeline: any) => (
                      <MenuItem key={pipeline.id} value={pipeline.id}>{pipeline.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )} />

              <div className="lead-status flex gap-2 items-center flex-1 w-full">
                {Object.values(TaskStatus).map((status: TaskStatus) => (
                  <div key={status} className="flex-1">
                    <Button
                      size="small"
                      fullWidth
                      key={status}
                      variant={methods.watch('status') === status ? "contained" : "outlined"}
                      color={getStatusColor(status)}
                      onClick={() => methods.setValue('status', status)}>
                      <span className="text-nowrap">{t(`task.status.${status}`)}</span>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="lead-body mt-6">
              <Tabs value={selectedTab} onChange={(e, value) => setSelectedTab(value)}>
                <Tab label="Основное" value="main" />
              </Tabs>
              <section className="mt-4">
                {selectedTab === 'main' && <TaskMainInfo
                  task={task}
                  members={membersResponse?.data || []}
                />
                }
              </section>
            </div>

          </section>
          {/* <pre>{JSON.stringify(methods.watch(), null, 2)}</pre> */}
        </form>
      </FormProvider>
      <Dialog fullWidth maxWidth="sm" open={isOpenDeleteTask} onClose={() => { setIsOpenDeleteTask(false) }}>
        <DialogTitle>Удалить задачу</DialogTitle>
        <DialogContent>
          <div>Вы уверены, что хотите удалить эту задачу?</div>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={onDeleteTask}>Удалить</Button>
          <Button variant="outlined" color="info" onClick={() => { setIsOpenDeleteTask(false) }}>Отмена</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

